import React, { useState } from 'react';
import { Crown, UserX } from 'lucide-react';
import * as miniLeaguesApi from '../../api/miniLeagues';

/**
 * The two halves of manager succession, kept together because they are the same
 * banner in two states.
 *
 * Vacant: every member sees the vacancy and can take the job. The league keeps
 * running the games it already has, but no new tournament can be added and no
 * game is recreated when the current one finishes — that stall is deliberate,
 * it is what produces a volunteer.
 *
 * Managed: the manager gets a way out. Only offered between games; the backend
 * refuses while anything is under way and the reason it gives is shown as-is.
 */
function LeagueVacancy({ league, isManager, isMember, onChanged, mode = 'banner' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (action) => {
    setError('');
    setBusy(true);
    try {
      await action();
      if (onChanged) onChanged();
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.body?.error || 'That did not work');
      } else {
        setError('Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  };

  // The vacancy banner belongs at the top of the page; the manager's way out
  // belongs down with the other manager controls. Same feature, two placements.
  if (mode === 'banner' && !league.vacant) return null;
  if (mode === 'actions' && league.vacant) return null;

  if (league.vacant) {
    return (
      <div className="league-notice league-vacancy">
        <Crown size={18} />
        <div className="league-vacancy-body">
          <strong>This league has no manager.</strong>
          <span>
            Nobody can add a tournament, and the next game won't start, until
            someone takes over.
          </span>
          {error && <span className="error-text">{error}</span>}
        </div>
        {isMember && (
          <button
            className="primary-btn"
            disabled={busy}
            onClick={() => run(() => miniLeaguesApi.claimLeague(league.id))}
          >
            Take the job
          </button>
        )}
      </div>
    );
  }

  if (!isManager) return null;

  return (
    <div className="league-manager-actions">
      {error && <p className="error-text">{error}</p>}
      <button
        className="secondary-btn"
        disabled={busy}
        onClick={() => {
          if (!window.confirm(
            'Step down as manager?\n\nThe league stays put, but nobody can add a '
            + 'tournament or start the next game until a member takes over — and '
            + 'that could be someone else.'
          )) {
            return;
          }
          run(() => miniLeaguesApi.resignAsManager(league.id));
        }}
      >
        <UserX size={16} />
        Step down as manager
      </button>
    </div>
  );
}

export default LeagueVacancy;
